import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createCheckoutSession } from '../services/stripeService';
import { PRODUCTS } from '../stripe-config';
import { CreditCard, CheckCircle, ArrowLeft, Loader2, Tag, X } from 'lucide-react';
import { getCurrentUser } from '../services/authService';
import { supabase } from '../services/db';

const MAX_POLL_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 3000;

const PaymentRequiredPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [hasValidSession, setHasValidSession] = useState(false);
  const [voucherInput, setVoucherInput] = useState('');
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [voucherError, setVoucherError] = useState<string | null>(null);
  const [voucherSuccess, setVoucherSuccess] = useState<string | null>(null);
  const [showVoucherInput, setShowVoucherInput] = useState(false);

  const isCheckoutSuccess = searchParams.get('checkout') === 'success';
  const [pollStatus, setPollStatus] = useState<'idle' | 'polling' | 'redirecting' | 'timeout'>('idle');
  const pollAttemptRef = useRef(0);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPollingRef = useRef(false);

  // Session-Check beim Laden der Seite
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      const valid = !!data.session;
      setHasValidSession(valid);
      setSessionChecked(true);
      if (!valid) {
        navigate('/login', { replace: true });
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Separate Effect: immer weiterleiten wenn user freigeschaltet ist,
  // unabhaengig davon ob checkout=success in URL steht.
  // Verhindert, dass die Seite stehen bleibt wenn Context-State spaet aktualisiert wird.
  useEffect(() => {
    if (user && user.isDisabled === false) {
      navigate('/kurszugang', { replace: true });
    }
  }, [user, navigate]);

  // Polling-Flow: nur starten wenn checkout=success, User eingeloggt und noch deaktiviert
  useEffect(() => {
    if (!isCheckoutSuccess) return;
    if (!isAuthenticated || !user) return;

    // Initial-Check: wenn user schon freigeschaltet, sofort weiterleiten
    if (user.isDisabled === false) {
      navigate('/kurszugang', { replace: true });
      return;
    }

    // Kein doppeltes Polling starten
    if (isPollingRef.current) return;
    isPollingRef.current = true;

    setPollStatus('polling');
    pollAttemptRef.current = 0;

    const poll = async () => {
      pollAttemptRef.current += 1;

      try {
        const freshUser = await getCurrentUser();
        if (freshUser && freshUser.isDisabled === false) {
          setUser(freshUser);
          setPollStatus('redirecting');
          navigate('/kurszugang', { replace: true });
          return;
        }
      } catch {
        // silent - wird beim naechsten Versuch wiederholt
      }

      if (pollAttemptRef.current >= MAX_POLL_ATTEMPTS) {
        // Letzter Versuch vor Timeout
        try {
          const latestUser = await getCurrentUser();
          if (latestUser && latestUser.isDisabled === false) {
            setUser(latestUser);
            setPollStatus('redirecting');
            navigate('/kurszugang', { replace: true });
            return;
          }
        } catch {
          // silent
        }
        isPollingRef.current = false;
        setPollStatus('timeout');
        // URL bereinigen: checkout=success entfernen, damit ein Refresh nicht erneut den Polling-Flow startet
        navigate('/payment-required', { replace: true });
        return;
      }

      pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    };

    pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);

    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
    // Absichtlich keine user?.isDisabled in den Dependencies: Polling soll nur einmal starten,
    // nicht bei jedem Context-Update neu. Der separate Effect oben handelt Context-Updates.
  }, [isCheckoutSuccess, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!sessionChecked || !isAuthenticated || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Lade Kontoinformationen...</p>
        </div>
      </div>
    );
  }

  if (!hasValidSession) {
    return null;
  }

  const handleVoucherApply = async () => {
    const rawInput = voucherInput;
    const code = rawInput.trim();

    console.log('[VoucherApply] rawInput:', JSON.stringify(rawInput));
    console.log('[VoucherApply] trimmed+uppercased code:', JSON.stringify(code));

    if (!code) {
      console.log('[VoucherApply] code is empty after trim → showing required error');
      setVoucherError('Bitte geben Sie einen Gutscheincode ein.');
      return;
    }

    setVoucherLoading(true);
    setVoucherError(null);
    setVoucherSuccess(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session || !user) {
        navigate('/login', { replace: true });
        return;
      }

      console.log('[VoucherApply] Calling validate-voucher with code:', JSON.stringify(code));

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/validate-voucher`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ code }),
        }
      );

      console.log('[VoucherApply] Response status:', response.status);
      const result = await response.json();
      console.log('[VoucherApply] Response body:', JSON.stringify(result));

      if (!response.ok || !result.valid) {
        setVoucherError(result.error || 'Ungültiger Gutscheincode.');
        return;
      }

      const { error: updateError } = await supabase
        .from('users')
        .update({ voucher_code: code })
        .eq('id', user.id);

      if (updateError) {
        setVoucherError('Gutscheincode konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.');
        return;
      }

      const freshUser = await getCurrentUser();
      if (freshUser) {
        setUser(freshUser);
      }

      setVoucherSuccess(`Gutscheincode "${code}" wurde erfolgreich angewendet.`);
      setVoucherInput('');
      setShowVoucherInput(false);
    } catch (err) {
      console.error('[VoucherApply] Exception:', err);
      setVoucherError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setVoucherLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!isAuthenticated || !user) {
      navigate('/login');
      return;
    }

    if (user.isDisabled === false) {
      navigate('/kurszugang', { replace: true });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        navigate('/login', { replace: true });
        return;
      }

      const billingAddress = {
        street: user.street || '',
        city: user.city || '',
        postalCode: user.postalCode || ''
      };

      const voucherCode = user.voucher_code || null;

      const checkoutUrl = await createCheckoutSession(
        PRODUCTS.KI_KOMPETENZNACHWEIS.priceId,
        'payment',
        billingAddress,
        voucherCode
      );

      window.location.href = checkoutUrl;

    } catch (error) {
      console.error('Payment initiation error:', error);
      setError(error instanceof Error ? error.message : 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  const isPollingActive = pollStatus === 'polling' || pollStatus === 'redirecting';
  const userIsDisabled = user.isDisabled !== false;

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Zurück zum Login
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">

            {(isCheckoutSuccess || pollStatus === 'timeout') && (
              <div className={`mb-6 p-4 rounded-md border flex items-start gap-3 ${
                pollStatus === 'timeout'
                  ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
                  : 'bg-green-50 border-green-200 text-green-800'
              }`}>
                {pollStatus === 'redirecting' ? (
                  <CheckCircle className="w-5 h-5 mt-0.5 shrink-0 text-green-600" />
                ) : pollStatus === 'timeout' ? null : (
                  <Loader2 className="w-5 h-5 mt-0.5 shrink-0 animate-spin text-green-600" />
                )}
                <p className="text-sm font-medium">
                  {pollStatus === 'redirecting' && 'Ihr Zugang ist jetzt aktiv. Sie werden weitergeleitet ...'}
                  {pollStatus === 'polling' && 'Ihre Zahlung war erfolgreich. Ihr Zugang wird jetzt freigeschaltet ...'}
                  {pollStatus === 'timeout' && 'Die Zahlung war erfolgreich. Die Freischaltung dauert noch einen Moment. Bitte aktualisieren Sie die Seite in wenigen Sekunden.'}
                  {pollStatus === 'idle' && isCheckoutSuccess && 'Ihre Zahlung war erfolgreich.'}
                </p>
              </div>
            )}

            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-8 h-8 text-orange-600" />
              </div>
              <h1 className="text-3xl font-bold text-gray-800 mb-4">
                Zahlung erforderlich
              </h1>
              <p className="text-lg text-gray-600">
                Willkommen zurück, {user.name}! Um Zugang zu Ihrem KI-Kompetenznachweis zu erhalten,
                ist eine Zahlung erforderlich.
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md border border-red-200">
                {error}
                {(error.toLowerCase().includes('abgelaufen') ||
                  error.toLowerCase().includes('ungültig') ||
                  error.toLowerCase().includes('expired') ||
                  error.toLowerCase().includes('rabatt')) && (
                  <span>
                    {' '}Bitte wenden Sie sich an den Support unter{' '}
                    <a
                      href="mailto:aiko@ki-navigatoren.de"
                      className="underline hover:text-red-900"
                    >
                      aiko@ki-navigatoren.de
                    </a>
                    .
                  </span>
                )}
              </div>
            )}

            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-blue-800 mb-3">
                  Ihr KI-Kompetenznachweis
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span className="text-blue-700">7 interaktive Lernmodule (ca. 45 Minuten)</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span className="text-blue-700">Abschlusstest mit sofortigem Ergebnis</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span className="text-blue-700">Personalisiertes Zertifikat</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span className="text-blue-700">Rechtssicherheit nach EU AI Act</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                  Preis
                </h3>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">KI-Kompetenznachweis</span>
                  <span className="text-2xl font-bold text-gray-800">49,90 € zzgl. MwSt.</span>
                </div>

                {user.voucher_code ? (
                  <div className="mt-3 flex items-center justify-between bg-green-50 border border-green-200 rounded-md px-3 py-2">
                    <div className="flex items-center gap-2 text-sm text-green-700">
                      <Tag className="w-4 h-4 shrink-0" />
                      <span>Gutscheincode <strong>"{user.voucher_code}"</strong> wird angewendet</span>
                    </div>
                    <button
                      onClick={() => { setShowVoucherInput(true); setVoucherError(null); setVoucherSuccess(null); }}
                      className="text-xs text-gray-500 hover:text-gray-700 underline ml-3 shrink-0"
                    >
                      ändern
                    </button>
                  </div>
                ) : (
                  <div className="mt-3">
                    {voucherSuccess && (
                      <div className="mb-2 text-sm text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        {voucherSuccess}
                      </div>
                    )}
                    {!showVoucherInput ? (
                      <button
                        onClick={() => setShowVoucherInput(true)}
                        className="text-sm text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
                      >
                        <Tag className="w-4 h-4" />
                        Gutscheincode eingeben
                      </button>
                    ) : null}
                  </div>
                )}

                {showVoucherInput && (
                  <div className="mt-3">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={voucherInput}
                        onChange={(e) => setVoucherInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleVoucherApply(); }}
                        placeholder="Gutscheincode eingeben"
                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        disabled={voucherLoading}
                      />
                      <button
                        onClick={handleVoucherApply}
                        disabled={voucherLoading || !voucherInput.trim()}
                        className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        {voucherLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Anwenden'}
                      </button>
                      <button
                        onClick={() => { setShowVoucherInput(false); setVoucherInput(''); setVoucherError(null); }}
                        className="p-2 text-gray-400 hover:text-gray-600"
                        disabled={voucherLoading}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    {voucherError && (
                      <p className="mt-1 text-sm text-red-600">
                        {voucherError}{' '}
                        {(voucherError.toLowerCase().includes('abgelaufen') ||
                          voucherError.toLowerCase().includes('expired') ||
                          voucherError.toLowerCase().includes('not active') ||
                          voucherError.toLowerCase().includes('no remaining') ||
                          voucherError.toLowerCase().includes('not found')) && (
                          <span>
                            Bitte wenden Sie sich an den Support unter{' '}
                            <a
                              href="mailto:aiko@ki-navigatoren.de"
                              className="underline hover:text-red-800"
                            >
                              aiko@ki-navigatoren.de
                            </a>
                            .
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {userIsDisabled && (
                <div className="text-center">
                  <button
                    onClick={handlePayment}
                    disabled={loading || isPollingActive}
                    className={`w-full bg-[#EE7F01] hover:bg-orange-600 text-white font-medium py-4 px-8 rounded-lg text-xl transition-all duration-300 transform hover:scale-105 hover:shadow-lg ${
                      loading || isPollingActive ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <Loader2 className="w-6 h-6 mr-2 animate-spin" />
                        Weiterleitung zur Zahlung...
                      </div>
                    ) : (
                      'Jetzt bezahlen und starten'
                    )}
                  </button>
                </div>
              )}

              <div className="text-center">
                <p className="text-sm text-gray-500">
                  Sichere Zahlung über Stripe • Sofortiger Zugang nach Zahlung
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentRequiredPage;
